import {
  createContext,
  useState,
  ReactNode,
  useContext,
  useMemo,
  useEffect,
} from 'react'
import { Storage } from '../configs/constant'
import { useTranslation } from 'react-i18next'
import { TOKEN_INFO_USER_INDEX } from '../configs/constant'

interface AppContextType {
  token: string
  apiKey: string
  updateToken: Function
  isAuthenticated: boolean
  language: string
  updateLanguage: Function
  emailUser: string
  username: string
  isTenant: boolean
  isMaster: boolean
  domainLink: string
  filters: Record<string, any>
  setFilters: Function
  headerCenterMessage: string
  setHeaderCenterMessage: Function
  customLogoUrl: string
  setCustomLogoUrl: (url: string) => void
  logout: Function
}

interface AppContextProviderProps {
  children: ReactNode
}

const defaultContextValue: AppContextType = {
  token: '',
  apiKey: '',
  updateToken: () => { },
  isAuthenticated: false,
  language: '',
  updateLanguage: () => { },
  emailUser: '',
  username: '',
  isTenant: false,
  isMaster: false,
  domainLink: '',
  filters: {},
  setFilters: () => { },
  headerCenterMessage: '',
  setHeaderCenterMessage: () => { },
  customLogoUrl: '',
  setCustomLogoUrl: () => { },
  logout: () => { }
}

export const AppContext = createContext<AppContextType>(defaultContextValue)
export const useAppContext = () => useContext(AppContext)

export const AppContextProvider = ({ children }: AppContextProviderProps) => {
  const [token, setToken] = useState<string>(
    localStorage.getItem(Storage.TOKEN) || ''
  )
  const [apiKey, setApiKey] = useState<string>(
    localStorage.getItem(Storage.API_KEY) || ''
  )
  const [language, setLanguage] = useState<string>(
    localStorage.getItem(Storage.LANGUAGE) || 'ja'
  )
  const [headerCenterMessage, setHeaderCenterMessage] = useState<string>('')
  const { i18n } = useTranslation()
  const [filters, setFilters] = useState<Record<string, any>>({
    limit: 12,
    orderBy: 'CreatedDate,desc',
  })
  const [customLogoUrl, setCustomLogoUrl] = useState<string>('')

  const isAuthenticated = useMemo<boolean>(() => {
    return !!token
  }, [token])

  const userInformation = useMemo(() => {
    if (!token) return null
    return JSON.parse(atob(token.split('.')[TOKEN_INFO_USER_INDEX]))
  }, [token])

  const emailUser = useMemo(() => {
    return userInformation?.email
  }, [userInformation])

  const isTenant = useMemo(() => {
    return userInformation?.isTenant
  }, [userInformation])

  const domainLink = useMemo(() => {
    return userInformation?.domainLink
  }, [userInformation])

  const username = useMemo(() => {
    return userInformation?.username
      ? decodeURIComponent(userInformation?.username)
      : ''
  }, [userInformation])

  const isMaster = useMemo(() => {
    return userInformation?.isMaster
  }, [userInformation])

  const updateToken = (token: string, apiKeyRes: string) => {
    setApiKey(apiKeyRes)
    setToken(token)
    setCustomLogoUrl('')
    localStorage.setItem(Storage.API_KEY, apiKeyRes)
    localStorage.setItem(Storage.TOKEN, token)
  }

  const updateLanguage = (lang: string) => {
    setLanguage(lang || 'ja')
    localStorage.setItem(Storage.LANGUAGE, lang)
  }

  useEffect(() => {
    language && i18n.changeLanguage(language)
  }, [language, i18n])

  useEffect(() => {
    if (isAuthenticated && domainLink) {
      const savedLogo = localStorage.getItem(`customLogoUrl_${domainLink}`);
      if (savedLogo) {
        setCustomLogoUrl(savedLogo);
      } else {
        setCustomLogoUrl('');
      }
    } else {
      setCustomLogoUrl('');
    }
  }, [isAuthenticated, domainLink]);

  useEffect(() => {
    if (isAuthenticated && domainLink) {
      if (customLogoUrl) {
        localStorage.setItem(`customLogoUrl_${domainLink}`, customLogoUrl);
      } else {
        localStorage.removeItem(`customLogoUrl_${domainLink}`);
      }
    }
  }, [customLogoUrl, isAuthenticated, domainLink]);

  const logout = () => {
    setToken('');
    setApiKey('');
    setCustomLogoUrl('');
    
    localStorage.clear();
    
    window.location.href = '/login';
  };

  const expectedValue: AppContextType = {
    token,
    apiKey,
    updateToken,
    isAuthenticated,
    language,
    updateLanguage,
    emailUser,
    username,
    isTenant,
    isMaster,
    domainLink,
    filters,
    setFilters,
    headerCenterMessage,
    setHeaderCenterMessage,
    customLogoUrl,
    setCustomLogoUrl,
    logout
  }

  return (
    <AppContext.Provider value={expectedValue}>{children}</AppContext.Provider>
  )
}
